<template>
  <div class="oct-user-form oct-grid">
    <div class="oct-grid__row">
      <div class="oct-user-form-header oct-grid__col oct-grid__col--12 oct-grid__col--dense">
        <oct-article-header
          :title="type === 'artist' ? 'MYPAGE. ARTIST' : (artfair ? 'MYPAGE. ART FAIR' : 'MYPAGE. GALLERY')"
        />

        <div class="oct-user-form-header__action">
          <oct-switch
            value="private"
            v-model="$_octUserForm_state"
            label="公開"
          />
        </div>
      </div>

      <div class="oct-grid__col oct-grid__col--6">
        <oct-section-header title="PROFILE" />

        <!-- @event 'submit' -->
        <oct-uploader
          class="oct-user-form__profile-icon"
          :token="this.token"
          :image.sync="$_octUser_profileIcon"
          size="small"
          @uploaded="$emit('submit')"
        />

        <div class="oct-user-form-profile">
          <div class="oct-user-form-profile__text">
            <span class="oct-user-form-profile__title-text">{{ tradeName }}</span>
            <h2 class="oct-user-form-profile__primary-text">{{ tradeNameEN }}</h2>
            <p
              class="oct-user-form-profile__secondary-text"
              v-if="type === 'artist'"
            >
              {{ position.join(', ') }}{{ base ? ` / ${base}` : '' }}{{ birthday ? ', b. ' : '' }}{{ birthday.substr(0, 4) }}
            </p>
            
            <p
              class="oct-user-form-profile__secondary-text"
              v-else
            >
              {{ base }} / est. {{ birthday.substr(0, 4) }}
            </p>
          </div>

          <div class="oct-user-form-profile__links">
            <oct-icon-button :href="website" target="_blank" icon="language" size="small" :outline="true" v-if="website !== ''" />
            <oct-icon-button :href="instagram" target="_blank" icon="instagram" size="small" :outline="true" v-if="instagram !== ''" />
            <oct-icon-button :href="twitter" target="_blank" icon="twitter" size="small" :outline="true" v-if="twitter !== ''" />
            <oct-icon-button :href="facebook" target="_blank" icon="facebook" size="small" :outline="true" v-if="facebook !== ''" />
          </div>

          <div class="oct-user-form-profile__action">
            <oct-button
              href="/account/profile/"
              text="Edit"
            />
          </div>
        </div>
      </div>

      <div class="oct-grid__col oct-grid__col--6">
        <oct-section-header title="COVER" />

        <!-- @event 'submit' -->
        <oct-uploader
          class="oct-user-form__cover-image"
          :class="{
            'oct-user-form__cover-image--completed': $_octUser_coverImage.lowResolution
          }"
          :token="this.token"
          :image.sync="$_octUser_coverImage"
          @uploaded="$emit('submit')"
        />
      </div>

      <div class="oct-grid__col oct-grid__col--12">
        <oct-section-header title="BIO" />

        <div class="oct-user-form-bio">
          <div class="oct-user-form-bio__text">
            <h2 class="oct-user-form-bio__primary-text">{{ getLocaleString(null, 'title') }}</h2>
            <p class="oct-user-form-bio__secondary-text">
              {{ getLocaleString(null, 'description') }}
              <br v-if="getLocaleString(null, 'description') && getLocaleString(data, 'history')">
              <br v-if="getLocaleString(null, 'description') && getLocaleString(data, 'history')">
              {{ getLocaleString(data, 'history') }}
            </p>
          </div>

          <oct-button
            href="/account/bio/"
            text="Edit"
          />
        </div>
      </div>

      <div class="oct-grid__col oct-grid__col--12">
        <oct-section-header title="RELATED CATEGORIES" />
 
        <div class="oct-user-form-tags">
          <div class="oct-user-form-tags__text">
            <oct-chip-set v-bind:items="tagItems">
              <template v-slot:chip="{ item }">
                <oct-chip v-bind="item"></oct-chip>
              </template>
            </oct-chip-set>
          </div>

          <oct-button
            href="/account/categories/"
            text="Edit"
          />
        </div>       
      </div>

      <!--
        Artworks
        Not shown for art fair accounts
      -->
      <div
        class="oct-grid__col oct-grid__col--12"
        v-if="!artfair"
      >
        <oct-section-header
          title="ARTWORKS"
          :meta="{
            text: 'VIEW ALL',
            href: '/account/your/#artworks'
          }"
        />
 
        <div class="oct-user-form-artworks">
          <oct-card-list>
            <oct-card-list-item>
              <oct-add-circle :action="{href: '/account/artwork/'}" />
            </oct-card-list-item>

            <oct-card-list-item
              v-for="(item, i) in artworks"
              :key="i"
            >
              <oct-card
                :media="{
                  image: item.images[0],
                  lock: item.status === 'private'
                }"
                :href="`/account/artwork/#${item.id}`"
                :headline="getLocaleString(item, 'title')"
                :caption="`${item.publishedAt} / ${item.materials.join(', ')}`"
              >
              </oct-card>
            </oct-card-list-item>
          </oct-card-list>
        </div>       
      </div>

      <!--
        Exhibitions
        Not shown for art fair accounts
      -->
      <div
        class="oct-grid__col oct-grid__col--12"
        v-if="!artfair"
      >
        <oct-section-header
          title="EXHIBITIONS"
          :meta="{
            text: 'VIEW ALL',
            href: '/account/your/#exhibitions'
          }"
        />
 
        <div class="oct-user-form-artworks">
          <oct-card-list>
            <oct-card-list-item>
              <oct-add-circle :action="{href: '/account/exhibition/'}" />
            </oct-card-list-item>

            <oct-card-list-item
              v-for="(item, i) in exhibitions"
              :key="i"
            >
              <oct-card
                :media="{
                  image: item.images[0],
                  lock: item.status === 'private'
                }"
                :href="`/account/exhibition/#${item.id}`"
                :title="`Exhibition: ${item.type}`"
                :headline="getLocaleString(item, 'title') + '\n' + dateToString(new Date(item.startDate))"
                :text="getLocaleString(item.gallery, 'tradeName')"
              >
              </oct-card>
            </oct-card-list-item>
          </oct-card-list>
        </div>       
      </div>

      <!--
        Art fairs 
        Only for art fair accounts
      -->
      <div
        class="oct-grid__col oct-grid__col--12"
        v-if="artfair"
      >
        <oct-section-header
          title="ART FAIRS & EVENTS"
          :meta="{
            text: 'VIEW ALL',
            href: '/account/your/#artfairs'
          }"
        />
 
        <div class="oct-user-form-artworks">
          <oct-card-list>
            <oct-card-list-item>
              <oct-add-circle :action="{href: '/account/artfair/'}" />
            </oct-card-list-item>

            <oct-card-list-item
              v-for="(item, i) in artfairs"
              :key="i"
            >
              <oct-card
                :media="{
                  image: item.images[0],
                  lock: item.status === 'private'
                }"
                :href="`/account/artfair/#${item.id}`"
                title="Art Fair / Event"
                :headline="getLocaleString(item, 'title') + '\n' + dateToString(new Date(item.startDate))"
                :text="getLocaleString(item.gallery, 'tradeName')"
              >
              </oct-card>
            </oct-card-list-item>
          </oct-card-list>
        </div>       
      </div>

      <!-- News -->
      <div class="oct-grid__col oct-grid__col--12">
        <oct-section-header
          title="PRESS RELEASES"
          :meta="{
            text: 'VIEW ALL',
            href: '/account/your/#prs'
          }"
        />
 
        <div class="oct-user-form-artworks">
          <oct-card-list>
            <oct-card-list-item>
              <oct-add-circle :action="{href: '/account/pr/'}" />
            </oct-card-list-item>

            <oct-card-list-item
              v-for="(item, i) in news"
              :key="i"
            >
              <oct-card
                :media="{
                  image: item.images[0],
                  lock: item.status === 'private'
                }"
                :href="`/account/pr/#${item.id}`"
                :title="`Exhibition: ${item.type}`"
                :headline="getLocaleString(item, 'newsTitle')"
                :text="dateToString(new Date(item.publishedAt))"
              >
              </oct-card>
            </oct-card-list-item>
          </oct-card-list>
        </div>       
      </div>
    </div>
  </div>
</template>

<script>
import OctUser from '@/components/user/OctUser.js'
import OctPrototypeForm from '@/components/prototype-form/OctPrototypeForm.js'
import OctSwitch from '@/components/switch/OctSwitch';
import OctChipSet from '@/components/chips/OctChipSet.vue';
import OctChip from '@/components/chips/OctChip.vue';
import OctCardList from '@/components/card/OctCardList.vue';
import OctCardListItem from '@/components/card/OctCardListItem.vue';
import OctCard from '@/components/card/OctCard.vue';
import OctAddCircle from '@/components/add-circle/OctAddCircle.vue';
import OctArticleHeader from '@/components/article-header/OctArticleHeader.vue';
import OctSectionHeader from '@/components/section-header/OctSectionHeader.vue';
import OctUploader from '@/components/uploader/OctUploader.vue'

export default {
  mixins: [OctUser, OctPrototypeForm],
  name: 'OctUserForm',
  components: {
    OctSwitch,
    OctChipSet,
    OctChip,
    OctCardList,
    OctCardListItem,
    OctCard,
    OctAddCircle,
    OctUploader,
    OctArticleHeader,
    OctSectionHeader
  },
  props: {
    /** User's artworks */
    artworks: {
      type: Array,
      default: () => []
    },
    /** User's exhibitions */
    exhibitions: {
      type: Array,
      default: () => []
    },
    /** User's art fairs */
    artfairs: {
      type: Array,
      default: () => []
    },
    /** User's news */
    news: {
      type: Array,
      default: () => []
    },
    /** Auth0 access token */
    token: {
      type: String,
      default: ''
    }
  },
  computed: {
    /** Convert tag into tag object */
    tagItems () {
      return this.tags.reduce((acc, cur) => {
        // Get tag object and push tag if available
        const tag = this.categoriesBySlug[cur]
        tag && acc.push({text: tag.name})

        return acc
      }, [])
    },
    /** User status */
    $_octUserForm_state: {
      get () {
        return this.$_octUser_data.status === 'public'
      },
      set (val) {
        this.$_octUser_data.status = val ? 'public' : 'private'

        if (this.$_octUser_tradeNameEN === '' && this.$_octUser_tradeName === '') {
          alert(`${this.type === 'artist' ? 'アーティスト' : 'ギャラリー'}名を入力してください`)
          // Revert change
          return this.$nextTick(() => this.$_octUser_data.status = 'private')
        }

        if (this.$_octUser_profileIcon.lowResolution === '') {
          alert('プロフィールアイコンを設定してください')
          // Revert change
          return this.$nextTick(() => this.$_octUser_data.status = 'private')
        }

        // Submit
        this.$emit('submit')
      }
    }
  },
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-user-form {
  margin-top:  oct-rem(72);
}

.oct-user-form-header {
  display: flex;
}

.oct-article-header {
  margin-bottom: 0;
  max-width: oct-rem(200);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin-bottom: oct-rem(112);
  max-width: none;
}
}

.oct-user-form-header__action {
  flex: 1 1 auto;
  text-align: right;
  margin: 0;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: oct-rem(12) 0 0 oct-rem(48)
}
}

.oct-user-form__profile-icon {
  margin: 0 0 oct-rem(16);
  width: oct-rem(64);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(48);
  width: oct-rem(128);
}
}

.oct-user-form-profile__text {
  margin: 0 0 oct-rem(16);
}

.oct-user-form-profile__title-text {
  font-size: oct-rem(13);
  font-weight: normal;
  line-height: (17/13);
  letter-spacing: 0;
  margin: (-3em/13) 0 ((-3em+12)/13);
  display: block;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(15);
  line-height: (20/15);
  margin: (-4em/15) 0 ((-4em+16)/15);
}
}

.oct-user-form-profile__primary-text {
  font-size: oct-rem(34);
  font-weight: normal;
  line-height: (36/34);
  letter-spacing: -.02em;
  margin: (-5em/34) 0 ((-5em+12)/34);
  display: block;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(47);
  line-height: (56/47);
  margin: (-10em/47) 0 ((-10em+16)/47);
}
}

.oct-user-form-profile__secondary-text {
  font-size: oct-rem(14);
  font-weight: normal;
  line-height: (17/14);
  letter-spacing: 0;
  margin: (-3em/14) 0;
  display: block;
  opacity: .40;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(18);
  line-height: (24/18);
  margin: (-5em/18) 0;
}
}

.oct-user-form-profile__links {
  margin: 0 0 oct-rem(32);
  display: flex;

  .oct-icon-button {
    margin-right: oct-rem(8);
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(48);
}
}

.oct-user-form__cover-image--completed {
  ::v-deep .oct-file-selector__circle,
  ::v-deep .oct-add-circle {
    border-radius: oct-rem(16);
  }
}

.oct-user-form-bio,
.oct-user-form-tags {
@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 0 calc((100% - 24px * 3) / 3 + 24px);
}
}

.oct-user-form-bio__text {
  margin: 0 0 oct-rem(32);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(48);
  padding: 0 oct-rem(51) 0 0;
}
}

.oct-user-form-bio__primary-text {
  font-size: oct-rem(19);
  font-weight: normal;
  line-height: (26/19);
  letter-spacing: -.02em;
  white-space: pre-wrap;
  margin: (-5em/19) 0 ((-5em+32)/19);
  display: block;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(30);
  line-height: (40/30);
  margin: (-3em/30) 0 ((-3em+48)/30);
}
}

.oct-user-form-bio__secondary-text {
  font-size: oct-rem(15);
  font-weight: normal;
  line-height: (24/15);
  letter-spacing: 0;
  white-space: pre-wrap;
  margin: (-6em/15) 0;
  display: block;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(17);
  line-height: (30/17);
  margin: (-8em/17) 0;
}
}

.oct-user-form-tags__text {
  margin: 0 0 oct-rem(32);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(48);
}
}
</style>
